import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';

import { makeStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';

import Tabs from '@apps/shared/src/components/Tabs';

import { RootState } from '../../store';
import { selectPage, selectReport, setPlanFilter } from '../reportActions';
import {
  monthlyReportingPage,
  planHealthReportingPage,
  providerAcceptanceReportingPage,
} from '../utils';

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  resetBtn: {
    color: '#ff0000',
    textDecoration: 'underline',
    padding: 0,
    border: 'none',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  statusTitle: {
    fontWeight: 400,
    lineHeight: '23px',
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0',
    color: '#797C80',
    '& .MuiTypography-body1': {
      fontSize: '1rem',
    },
  },
  caseStatus: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    textWrap: 'nowrap',
  },
  selectBox: {
    width: '100%',
    '& .MuiSelect-select': {
      backgroundColor: '#fff !important',
      border: '1px solid #E5EBF5',
      width: '100%',
      boxSizing: 'border-box',
    },
    // let clicks on the arrow fall through to the select so it opens
    '& .MuiSelect-icon': {
      pointerEvents: 'none',
    },
  },
  selectDiv: {
    width: '100%',
    marginBottom: '5px',
  },
});

type StateProps = {
  selectedReportPage: string;
  plans: any[];
};

const mapStateToProps = ({ reports, planSelect }: RootState): StateProps => ({
  selectedReportPage: reports.selectedReportPage,
  plans: planSelect.masterPlans,
});

const mapDispatchToProps = {
  selectPage,
  setPlanFilter,
  selectReport,
};

type Props = StateProps & typeof mapDispatchToProps;

const monthlyTabs = [
  { displayName: 'Active Cases', name: 'Active', page: monthlyReportingPage.activeCases },
  {
    displayName: 'Additional Monthly Payments',
    name: 'AdditionalMonthlyPayments',
    page: monthlyReportingPage.AdditionalMonthlyPayments,
  },
  {
    displayName: 'Claims By Month',
    name: 'ClaimsByMonth',
    page: monthlyReportingPage.claimsByMonth,
  },
  { displayName: 'Closed Cases', name: 'Closed', page: monthlyReportingPage.closedCases },
  { displayName: 'Totals Recap', name: 'TotalsRecap', page: monthlyReportingPage.totalsRecap },
];

const planHealthTabs = [
  { displayName: 'Cover Page', name: 'Active', page: planHealthReportingPage.coverPage },
  {
    displayName: 'Executive Summary',
    name: 'Executive Summary',
    page: planHealthReportingPage.ExecutiveSummary,
  },
  {
    displayName: 'High-Cost Drivers',
    name: 'High-Cost Drivers',
    page: planHealthReportingPage.HighCostDrivers,
  },
  {
    displayName: 'Claim Summary - All Time',
    name: 'Claim Summary - All Time',
    page: planHealthReportingPage.claimSummaryAllTime,
  },
  {
    displayName: 'Claim Summary - Plan Year',
    name: 'Claim Summary - Plan Year',
    page: planHealthReportingPage.claimSummaryPlanYear,
  },
  {
    displayName: 'Utilization - All Time',
    name: 'Utilization - All Time',
    page: planHealthReportingPage.utilizationAllTime,
  },
  {
    displayName: 'Utilization - Plan Year',
    name: 'Utilization - Plan Year',
    page: planHealthReportingPage.utilizationPlanYear,
  },
  {
    displayName: 'Balance Bill Summary - All Time',
    name: 'Balance Bill Summary - All Time',
    page: planHealthReportingPage.balanceBillSummaryAllTime,
  },
  {
    displayName: 'Balance Bill Summary - Plan Year',
    name: 'Balance Bill Summary - Plan Year',
    page: planHealthReportingPage.balanceBillSummaryPlanYear,
  },
  {
    displayName: 'Access Issue Summary - All Time',
    name: 'Access Issue Summary - All Time',
    page: planHealthReportingPage.accessIssueSummaryAllTime,
  },
  {
    displayName: 'Access Issue Summary - Plan Year',
    name: 'Access Issue Summary - Plan Year',
    page: planHealthReportingPage.accessIssueSummaryPlanYear,
  },
];

const providerAcceptanceTabs = [
  {
    displayName: 'State Summary',
    name: 'State Summary',
    page: providerAcceptanceReportingPage.stateSummary,
  },
  {
    displayName: 'Provider Summary',
    name: 'Provider Summary',
    page: providerAcceptanceReportingPage.providerSummary,
  },
];

const getInitialTab = (page: string, tabs: { name: string; page: string }[]) => {
  const foundTab = tabs.find(tab => tab.page === page);
  return foundTab ? foundTab.name : tabs[0]?.name || '';
};

function CaseStatusNav({
  selectedReportPage,
  selectPage,
  setPlanFilter,
  selectReport,
  plans,
}: Props): JSX.Element {
  const classes = useStyles();
  const location = useLocation();
  const [selectedPlan, setSelectedPlan] = useState('');
  const isPlanHealthReporting = location.pathname.includes('planHealth-reporting');
  const isProviderAcceptanceReporting = location.pathname.includes('providerAcceptance-reporting');

  let tabs = [];

  if (isPlanHealthReporting) {
    tabs = planHealthTabs;
  } else if (isProviderAcceptanceReporting) {
    tabs = providerAcceptanceTabs;
  } else {
    tabs = monthlyTabs;
  }

  const [tabValue, setTabValue] = useState(getInitialTab(selectedReportPage, tabs));
  const previousReportTypeRef = useRef<string | null>(null);

  useEffect(() => {
    let expectedDefaultPage = '';
    let currentReportType = '';

    if (isPlanHealthReporting) {
      expectedDefaultPage = planHealthReportingPage.coverPage;
      currentReportType = 'planHealth';
      selectReport('planHealth-reporting');
    } else if (isProviderAcceptanceReporting) {
      expectedDefaultPage = providerAcceptanceReportingPage.stateSummary;
      currentReportType = 'providerAcceptance';
      selectReport('providerAcceptance-reporting');
    } else {
      expectedDefaultPage = monthlyReportingPage.activeCases;
      currentReportType = 'monthly';
      selectReport('monthly-reporting');
    }

    const reportTypeChanged = previousReportTypeRef.current !== currentReportType;

    if (!selectedReportPage || reportTypeChanged) {
      selectPage(expectedDefaultPage);

      const initialTabName = getInitialTab(expectedDefaultPage, tabs);
      setTabValue(initialTabName);
    }

    previousReportTypeRef.current = currentReportType;

    if (plans && plans.length > 0 && !selectedPlan) {
      const initialPlan = plans[0];
      setSelectedPlan(initialPlan);
      setPlanFilter(initialPlan);
    }
  }, [
    isPlanHealthReporting,
    isProviderAcceptanceReporting,
    plans,
    selectedPlan,
    selectPage,
    setPlanFilter,
    tabs,
  ]);

  useEffect(() => {
    const initialPlan = plans && plans[0];
    setSelectedPlan(initialPlan);
    setPlanFilter(initialPlan);
  }, [location.pathname]);

  const handleTabChange = (value: string) => {
    setTabValue(value);
    const selected = tabs.find(tab => tab.name === value);
    if (selected) selectPage(selected.page);
  };

  const resetFilter = () => {
    const defaultTab = tabs[0];
    setTabValue(defaultTab.name);
    selectPage(defaultTab.page);
  };

  const handlePlanChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setSelectedPlan(value);
    setPlanFilter(value);
  };

  return (
    <>
      <div style={{ textAlign: 'right', margin: '1rem' }}>
        <button onClick={resetFilter} className={classes.resetBtn} type="button">
          Clear
        </button>
      </div>

      <div className={classes.statusTitle}>
        <Typography>Plan Name</Typography>
      </div>
      <div className={classes.selectDiv}>
        <Select
          id="plan-selector"
          value={selectedPlan}
          onChange={handlePlanChange}
          className={classes.selectBox}
          fullWidth
        >
          {plans &&
            plans.map(plan => (
              <MenuItem key={plan} value={plan}>
                {plan}
              </MenuItem>
            ))}
        </Select>
      </div>

      <div className={classes.statusTitle}>
        <Typography>Pages</Typography>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {tabs.map(tab => (
          <Tabs
            key={tab.name}
            onChange={handleTabChange}
            selectedValue={tabValue}
            options={[{ content: tab.displayName, value: tab.name }]}
          />
        ))}
      </div>
    </>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(CaseStatusNav);
